import React, { useState, useEffect } from 'react';
import { MessageSquare, TrendingUp, Users, Package, AlertTriangle, Calendar, BarChart3 } from 'lucide-react';
import { aiDashboardApi } from '../../api/aiDashboardApi';

interface SuggestedPromptsProps {
  onPromptSelect: (prompt: string) => void;
}

export const SuggestedPrompts: React.FC<SuggestedPromptsProps> = ({ onPromptSelect }) => {
  const [suggestedPrompts, setSuggestedPrompts] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchSuggestedPrompts = async () => {
      try {
        const response = await aiDashboardApi.getSuggestedPrompts();
        setSuggestedPrompts(response.data);
      } catch (error) {
        console.error('Error fetching suggested prompts:', error);
        // Fallback to powerful default prompts based on schema analysis
        setSuggestedPrompts([
          {
            category: 'Complaint Analysis',
            prompts: [
              'Show complaint severity distribution with cost impact correlation heatmap',
              'Analyze defect description patterns using text clustering and sentiment analysis',
              'Create complaint status flow visualization with resolution time metrics',
              'Display complaint recurrence patterns by customer and product combinations'
            ]
          },
          {
            category: 'Product Performance',
            prompts: [
              'Compare product complaint rates vs production volume with bubble chart',
              'Show product lifecycle impact on complaint frequency and severity trends',
              'Analyze product status changes correlation with complaint patterns',
              'Create product quality score based on complaint frequency and cost impact'
            ]
          },
          {
            category: 'Batch Quality Analysis',
            prompts: [
              'Show batch production date vs complaint frequency correlation matrix',
              'Analyze batch-level quality degradation patterns over time',
              'Create batch complaint clustering by production date proximity',
              'Display batch quality control effectiveness using statistical process control charts'
            ]
          },
          {
            category: 'Customer Insights',
            prompts: [
              'Identify high-value customers with complaint impact analysis',
              'Show customer complaint journey mapping with touchpoint analysis',
              'Analyze customer retention correlation with complaint resolution efficiency',
              'Create customer segmentation based on complaint behavior and product preferences'
            ]
          },
          {
            category: 'Time-based Analysis',
            prompts: [
              'Show seasonal complaint patterns with production date correlation',
              'Analyze complaint lag time from production to complaint registration',
              'Create time-series forecasting for complaint volume and cost trends',
              'Display complaint resolution time efficiency across different time periods'
            ]
          },
          {
            category: 'Cost Impact Analysis',
            prompts: [
              'Show cost impact distribution by severity level with statistical analysis',
              'Analyze ROI of quality improvements based on complaint cost reduction',
              'Create cost-benefit analysis of preventive vs reactive quality measures',
              'Display cost impact trends correlation with production and complaint patterns'
            ]
          }
        ]);
      } finally {
        setLoading(false);
      }
    };

    fetchSuggestedPrompts();
  }, []);

  const getCategoryIcon = (category: string) => {
    switch (category.toLowerCase()) {
      case 'complaint analysis':
        return <AlertTriangle className="h-5 w-5 text-red-500" />;
      case 'product performance':
        return <Package className="h-5 w-5 text-blue-500" />;
      case 'time-based analysis':
        return <Calendar className="h-5 w-5 text-green-500" />;
      case 'customer insights':
        return <Users className="h-5 w-5 text-purple-500" />;
      case 'batch quality analysis':
        return <BarChart3 className="h-5 w-5 text-orange-500" />;
      case 'cost impact analysis':
        return <TrendingUp className="h-5 w-5 text-emerald-500" />;
      case 'production correlation':
        return <Package className="h-5 w-5 text-indigo-500" />;
      default:
        return <BarChart3 className="h-5 w-5 text-gray-500" />;
    }
  };

  const getCategoryColor = (category: string) => {
    switch (category.toLowerCase()) {
      case 'complaint analysis':
        return 'from-red-50 to-red-100 border-red-200';
      case 'product performance':
        return 'from-blue-50 to-blue-100 border-blue-200';
      case 'time-based analysis':
        return 'from-green-50 to-green-100 border-green-200';
      case 'customer insights':
        return 'from-purple-50 to-purple-100 border-purple-200';
      case 'batch quality analysis':
        return 'from-orange-50 to-orange-100 border-orange-200';
      case 'cost impact analysis':
        return 'from-emerald-50 to-emerald-100 border-emerald-200';
      case 'production correlation':
        return 'from-indigo-50 to-indigo-100 border-indigo-200';
      default:
        return 'from-gray-50 to-gray-100 border-gray-200';
    }
  };

  if (loading) {
    return (
      <div className="max-w-6xl mx-auto">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {[...Array(4)].map((_, i) => (
            <div key={i} className="bg-white rounded-xl p-6 shadow-sm animate-pulse">
              <div className="h-4 bg-gray-200 rounded mb-4"></div>
              <div className="space-y-3">
                {[...Array(3)].map((_, j) => (
                  <div key={j} className="h-3 bg-gray-200 rounded"></div>
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto">
      <div className="text-center mb-8">
        <div className="flex items-center justify-center gap-2 mb-3">
          <MessageSquare className="h-6 w-6 text-blue-500" />
          <h2 className="text-2xl font-bold text-gray-800">Suggested Queries</h2>
        </div>
        <p className="text-gray-600">Click on any suggestion to get started, or type your own question</p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {suggestedPrompts.map((category, categoryIndex) => (
          <div
            key={categoryIndex}
            className={`bg-gradient-to-br ${getCategoryColor(category.category)} border rounded-xl p-6 shadow-sm hover:shadow-md transition-shadow`}
          >
            <div className="flex items-center gap-3 mb-4">
              {getCategoryIcon(category.category)}
              <h3 className="font-semibold text-gray-800">{category.category}</h3>
            </div>

            <div className="space-y-2">
              {category.prompts.slice(0, 4).map((prompt: string, promptIndex: number) => (
                <button
                  key={promptIndex}
                  onClick={() => onPromptSelect(prompt)}
                  className="w-full text-left p-3 bg-white/70 hover:bg-white rounded-lg text-sm text-gray-700 hover:text-gray-900 transition-all hover:shadow-sm border border-transparent hover:border-gray-200"
                >
                  {prompt}
                </button>
              ))}
            </div>
          </div>
        ))}
      </div>

      {/* Quick Examples */}
      <div className="mt-12 bg-white rounded-xl p-6 shadow-sm">
        <h3 className="text-lg font-semibold text-gray-800 mb-4 flex items-center gap-2">
          <TrendingUp className="h-5 w-5 text-blue-500" />
          Popular Examples
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
          {[
            "Show batch-level defect correlation patterns across production dates",
            "Analyze cost impact vs severity distribution by product category",
            "Create customer complaint journey mapping with repeat offender analysis",
            "Display production quality degradation trends by batch timeline",
            "Compare complaint resolution efficiency across different severity levels",
            "Show product lifecycle impact on complaint frequency and cost patterns",
            "Analyze seasonal production quality variations with batch correlation",
            "Create multi-dimensional view of customer-product-batch complaint clusters",
            "Display complaint escalation patterns from Register to Resolution status",
            "Show business impact correlation with production date proximity"
          ].map((example, index) => (
            <button
              key={index}
              onClick={() => onPromptSelect(example)}
              className="text-left p-3 bg-gray-50 hover:bg-blue-50 rounded-lg text-sm text-gray-700 hover:text-blue-700 transition-colors border border-gray-200 hover:border-blue-300"
            >
              "{example}"
            </button>
          ))}
        </div>
      </div>
    </div>
  );
};